import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFamilies } from '../services/familyService';
import { useAuth } from './AuthContext';
import { useCacheInvalidation } from './CacheInvalidationContext';

const FamilyContext = createContext();

export const useFamilies = () => {
    const context = useContext(FamilyContext);
    if (!context) {
        throw new Error('useFamilies must be used within a FamilyProvider');
    }
    return context;
};

export const FamilyProvider = ({ children }) => {
    const { center, role } = useAuth();
    const { registerRefresh } = useCacheInvalidation();
    const [families, setFamilies] = useState([]);
    const [loading, setLoading] = useState(true); // Start with true to show loading spinner on initial render
    const [error, setError] = useState(null);

    // Check if user needs family data (CTD and above, excludes Tutors)
    const needsFamilyData = role && ['SA', 'CA', 'CTA', 'CTD'].includes(role);

    const fetchFamilies = useCallback(async () => {
        // Don't set loading false if role isn't determined yet
        if (!role) {
            return; // Keep loading true while waiting for role
        }

        if (!center || !needsFamilyData) {
            setFamilies([]); // Clear families if user doesn't need the data
            setLoading(false); // Set loading to false if user doesn't need family data
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = await getFamilies();
            setFamilies(data || []);
        } catch (err) {
            setError(err);
            setFamilies([]);
        } finally {
            setLoading(false);
        }
    }, [center, needsFamilyData, role]);

    // Fetch families when center changes (only for roles that need it)
    useEffect(() => {
        fetchFamilies();
    }, [fetchFamilies]);

    // Poll for updates every 10 minutes (for infrequent webhook updates)
    useEffect(() => {
        if (!needsFamilyData) return;

        // Poll every 10 minutes, but only when page is visible
        const pollInterval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchFamilies();
            }
        }, 10 * 60 * 1000); // 10 minutes

        return () => clearInterval(pollInterval);
    }, [fetchFamilies, needsFamilyData]);

    // Refresh function to be called after CRUD operations
    const refreshFamilies = useCallback(() => {
        fetchFamilies();
    }, [fetchFamilies]);

    // Register refresh function for cache invalidation
    useEffect(() => {
        const cleanup = registerRefresh('families', refreshFamilies);
        return cleanup;
    }, [registerRefresh, refreshFamilies]);

    const value = {
        families,
        loading,
        error,
        refreshFamilies,
        fetchFamilies
    };

    return (
        <FamilyContext.Provider value={value}>
            {children}
        </FamilyContext.Provider>
    );
};

export default FamilyContext;
